import {
  useEffect,
  useMemo,
  useState,
} from 'react';

export const MISSING_VOW_ERROR_CODE = 'MISSING_VOW';

const buildMissingVowError = (acceptedVowConfigIds) => {
  const formatted = acceptedVowConfigIds.map((id) => `"${id}"`).join(' or ');
  const error = new Error(
    `No VOW was configured as ${formatted}, which is required for bulk upload.`,
  );
  error.code = MISSING_VOW_ERROR_CODE;
  return error;
};

const useBulkUploadTemplateConfig = ({
  getConfiguration,
  acceptedVowConfigIds,
}) => {
  const [configuration, setConfiguration] = useState(null);
  const [availableVows, setAvailableVows] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);

  const acceptedIds = useMemo(
    () => (Array.isArray(acceptedVowConfigIds) ? acceptedVowConfigIds : [acceptedVowConfigIds]),
    [acceptedVowConfigIds],
  );
  const acceptedIdsKey = acceptedIds.join('|');

  useEffect(() => {
    let isActive = true;
    setTemplateLoading(true);
    setTemplateError(null);

    const fetchConfiguration = async () => {
      const { configuration: config, vows } = await getConfiguration();
      if (!isActive) {
        return;
      }
      const customerVows = Array.isArray(vows) ? vows : [];
      const supportedVows = customerVows
        .filter((vow) => acceptedIds.includes(vow.vowConfigId));
      const defaultVow = supportedVows[0];
      const vowId = defaultVow?.vowId;
      if (!vowId) {
        setTemplateError(buildMissingVowError(acceptedIds));
      }
      setAvailableVows(supportedVows);
      setConfiguration({ ...config, vowId });
    };

    fetchConfiguration()
      .catch((error) => {
        console.error('Failed to load bulk upload configuration:', error);
        if (isActive) {
          setTemplateError(error);
        }
      })
      .finally(() => {
        if (isActive) {
          setTemplateLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [getConfiguration, acceptedIdsKey]);

  const ensureMissingVowError = () => {
    setTemplateError((previous) => previous || buildMissingVowError(acceptedIds));
  };

  return {
    configuration,
    availableVows,
    templateLoading,
    templateError,
    ensureMissingVowError,
  };
};

export default useBulkUploadTemplateConfig;
